import { Button, ButtonVariants } from '@tcc-online/common-components';
import { FormCheckbox, FormInput, Input } from '@tcc-online/input-components';
import { Form, Formik } from 'formik';
import { PropsWithChildren, useMemo } from 'react';
import { generatePath, Link, useNavigate } from 'react-router-dom';
import { useTranslate } from 'react-translate.ts';

import { IUser } from '../../../types';
import UserRoleSelect from '../../input/UserRoleSelect';
import BaseCard from '../BaseCard/BaseCard';
import styles from './UserCard.module.scss';
import useDate from '../../../hooks/useDate';
import { DateTime } from 'luxon';
import {
    TRANSLATIONS_BUTTONS_KEY,
    TRANSLATIONS_ROOT_KEY,
} from '../../../constants/translation.constants';
import {
    ADMIN_COMPANIES_PATH,
    ADMIN_COMPANY_PATH,
    ADMIN_PATH,
} from '../../../constants/route.constants';
import { useSelector } from 'react-redux';
import AuthSelectors from '../../../selectors/AuthSelectors';
import { AuthService } from '../../../services/AuthService';

const FIRST_NAME_FIELD = 'firstName';
const LAST_NAME_FIELD = 'lastName';
const TYPE_FIELD = 'type';
const EMAIL_FIELD = 'email';
const ACTIVE_FIELD = 'isActive';

type UserFormProps = {
    user: IUser | null;
    selfEdit?: boolean;
};

interface UserCardProps extends UserFormProps {
    onSubmit: (values: IUser) => void;
    isAdmin: boolean;
}

export default function UserCard(props: PropsWithChildren<UserCardProps>) {
    const { user, children, onSubmit, selfEdit, isAdmin } = props;
    const initialValues = getInitialValues(user);

    return (
        <BaseCard className={styles.card}>
            <div className={styles.form}>
                <div className={styles.form__image}>
                    <img
                        src={
                            user?.avatar || '/images/user-placeholder-temp.png'
                        }
                        alt=""
                    />
                </div>
                <Formik initialValues={initialValues} onSubmit={onSubmit}>
                    <UserForm
                        user={user}
                        selfEdit={selfEdit}
                        isAdmin={isAdmin}
                    />
                </Formik>
            </div>
            {children}
        </BaseCard>
    );
}

function getInitialValues(user: IUser | null) {
    if (user) {
        return user as Partial<IUser>;
    }

    return {
        id: 0,
        [FIRST_NAME_FIELD]: '',
        [LAST_NAME_FIELD]: '',
        [EMAIL_FIELD]: '',
        [TYPE_FIELD]: 'user',
        [ACTIVE_FIELD]: true,
    };
}

function UserForm(props: UserFormProps & { isAdmin: boolean }) {
    const { user, selfEdit, isAdmin } = props;
    const translate = useTranslate(TRANSLATIONS_ROOT_KEY + '.user');
    const accessToken = useSelector(AuthSelectors.getAuthToken);
    const companyId = useMemo(
        () => AuthService.getCompanyId(accessToken),
        [accessToken],
    );

    return (
        <Form>
            <Inputs />
            <div className={styles.form__check}>
                {!selfEdit ? (
                    <FormCheckbox
                        name={ACTIVE_FIELD}
                        label={translate('state')}
                        disabled={selfEdit}
                    />
                ) : (
                    <div />
                )}
                {user?.created && <CreatedDate />}
            </div>
            <LoginLinkInput />
            {isAdmin && companyId && (
                <Link
                    to={generatePath(
                        `${ADMIN_PATH}/${ADMIN_COMPANIES_PATH}/${ADMIN_COMPANY_PATH}`,
                        { companyId: companyId.toString() },
                    )}
                    relative="path"
                >
                    {translate('TODO: nazev firmy')} // TODO nazev firmy
                </Link>
            )}
            <Buttons />
        </Form>
    );

    function Inputs() {
        return (
            <div className={styles.form__basics}>
                <FormInput
                    name={FIRST_NAME_FIELD}
                    label={translate('first-name')}
                />
                <FormInput
                    name={LAST_NAME_FIELD}
                    label={translate('last-name')}
                />
                <FormInput
                    name={EMAIL_FIELD}
                    label={translate('email')}
                    required
                    disabled={selfEdit}
                />
                {!selfEdit ? (
                    <UserRoleSelect
                        name={TYPE_FIELD}
                        required
                        // @ts-ignore
                        disabled={selfEdit}
                    />
                ) : null}
            </div>
        );
    }

    function LoginLinkInput() {
        if (!user?.loginLink) {
            return null;
        }

        return (
            <Input
                name="loginUrl"
                label={translate('login-url')}
                value={user.loginLink}
                disabled
            />
        );
    }

    function CreatedDate() {
        if (!user) {
            return null;
        }

        const date = useDate(user.created, DateTime.DATE_MED);
        return (
            <div>
                {translate('created')}
                {`: ${date}`}
            </div>
        );
    }
}

function Buttons() {
    const translate = useTranslate(TRANSLATIONS_BUTTONS_KEY);
    const navigate = useNavigate();

    return (
        <div className={styles.form__buttons}>
            <Button
                variant={ButtonVariants.negative}
                onClick={() => navigate(-1)}
            >
                {translate('back')}
            </Button>
            <Button type="submit" variant={ButtonVariants.positive}>
                {translate('save')}
            </Button>
        </div>
    );
}
